import { catchError, delay, switchMap, take, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { Store } from '@ngxs/store';
import { InjectorProviderService } from 'src/app/_core/services/injector.provider.service';
import { UserState } from 'src/app/_user/store/user/user.state';
import { ContextInfosInput } from 'src/graphql/generated-types';
import { normalizeList } from 'src/app/utils/list.utils';

export abstract class ListDataSource<T> implements DataSource<T> {

    propertyId: keyof T;

    protected subscriptionsList: Subscription[] = [];

    protected skip = 0;

    protected numberOfElementsByPage = 200;

    protected filters = null;

    protected loading = new BehaviorSubject(false);

    protected store: Store;

    private _datastream: BehaviorSubject<T[]> = new BehaviorSubject([]);

    private total: BehaviorSubject<number> = new BehaviorSubject(0);

    constructor() {
        this.store = InjectorProviderService.get(Store);
    }

    private _cachedData: T[] = [];

    get cachedData(): T[] {
        return this._cachedData;
    }

    set cachedData(values: T[]) {
        this._cachedData.push(...values);
    }

    get loading$(): Observable<boolean> {
        return this.loading.asObservable();
    }

    get datastream$(): Observable<T[]> {
        return this._datastream.asObservable();
    }

    get userContext(): ContextInfosInput {
        return this.store.selectSnapshot(UserState.userContext);
    }

    loadingValue(): boolean {
        return this.loading.getValue();
    }

    abstract getList(params?: any): Observable<{ list: T[], total: number }>;

    reset(): void {
        this.total.next(0);
        this.loading.next(false);
        this.skip = 0;
        this._cachedData = [];
    }

    setFilters(filters: { [key: string]: any } = null): void {
        this.filters = filters;
    }

    /**
     * Return an observable that contains the items list
     */
    connect(collectionViewer: CollectionViewer): Observable<any> {
        this.subscriptionsList.push(
            collectionViewer.viewChange.pipe(
                delay(1), // Il faut mettre un delay pour qu'il puisse avoir le temps de faire un next sur le total avant de récupérer la liste
                switchMap((value: { start: number, end: number }) => {
                    if (value.end >= this.cachedData.length && this.skip < this.total.getValue() && !this.loading.getValue()) {
                        return this.changePage();
                    }
                    return of(false);
                })
            ).subscribe()
        );

        return this._datastream;
    }

    disconnect(collectionViewer: CollectionViewer): void {
        this.subscriptionsList.forEach(subscription => {
            if (subscription) {
                subscription.unsubscribe();
            }
        });
    }

    changePage(params?: any): Observable<any> {
        return this.getList(params).pipe(
            take(1),
            tap(({list, total}) => {
                    if (this.propertyId) {
                        list = normalizeList(list, this.propertyId);
                    }

                    this.skip += list.length;
                    this.cachedData = list;

                    this._datastream.next(this.cachedData);
                    this.total.next(total);
                    this.loading.next(false);
                }
            ),
            catchError(err => {
                this.loading.next(false);
                return err;
            }));
    }
}
